import { useState } from "react";
import { Stack, Box, Button, TableRow, TableCell, Typography } from "@mui/material";
import useDebtSalesTab from "../../hooks/useDebtSalesTab";
import TableComp from "src/components/shared/table/TableComp";
import DebtSalesFilter from "../../components/DebtSalesFilter";
import { DebtSalesPaymentDialog, DebtSalesHistoryDetailDialog } from "../Dialogs";
import { USER_TYPE } from "src/constant";
import SearchFilterComp from "src/components/shared/SearchFilterComp";
import { enqueueSnackbar } from "notistack";
import { formatCurrency } from "src/helper/convertNum";
import convertCurrency from "src/helper/convertCurrency";
import { DEBT_COLOR_INBOUND, DEBT_COLOR_OUTBOUND } from "../../utils/renderDebtAmountByInventoryType";

const DebtSalesAllTab = ({ date }) => {
  const [filter, setFilter] = useState({});
  const [openDetail, setOpenDetail] = useState(false);
  const [detailRow, setDetailRow] = useState(null);
  const [isMultiplePayment, setIsMultiplePayment] = useState(false);

  const handleViewDetail = (item) => {
    setDetailRow(item);
    setOpenDetail(true);
  };

  const handleCloseDetail = () => {
    setOpenDetail(false);
    setDetailRow(null);
  };

  const handleSearchKeyword = (value) => {
    setFilter((prev) => ({ ...prev, searchValue: value ?? undefined }));
  };

  const handleSetSaleSearch = (value) => {
    setFilter((prev) => ({ ...prev, sales: value ?? undefined }));
  };

  const handleSetCustomerSearch = (value) => {
    setFilter((prev) => ({ ...prev, customer: value ?? undefined }));
  };

  const handleSetSearchBy = (value) => {
    setFilter((prev) => ({ ...prev, searchField: value ?? undefined, searchValue: undefined }));
  };

  const { bodyList, sortData, header, pagi, setPagi, total, footerTotals, checkedList, selectedRow, setSelectedRow } = useDebtSalesTab({
    onViewDetail: handleViewDetail,
    date,
    filter,
  });

  const handleClosePayment = () => {
    setSelectedRow(null);
    setIsMultiplePayment(false);
  };

  const handleMultiplePayment = () => {
    if (checkedList.length) {
      const distinctCustomer = new Set(checkedList.map((item) => item?.customer?._id));
      if (distinctCustomer.size > 1) {
        enqueueSnackbar("Không thể thanh toán hàng loạt công nợ với khách hàng khác nhau", { variant: "error" });
        return;
      }
    }
    setIsMultiplePayment(true);
  };

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        my="14px"
      >
        <Box sx={{ width: "fit-content" }}>
          <SearchFilterComp
            width="fit-content"
            selectList={[
              { id: USER_TYPE.Sales, title: "Nhân viên kinh doanh", value: USER_TYPE.Sales },
              { id: USER_TYPE.Customer, title: "Khách hàng", value: USER_TYPE.Customer },
              { id: "contextCode", title: "Mã đơn hàng", value: "contextCode" },
              { id: "code", title: "Mã phiếu xuất", value: "code" },
            ]}
            getSearchKeyword={handleSearchKeyword}
            getSelectKeyword={handleSetSearchBy}
            autoComplete={{
              [USER_TYPE.Sales]: {
                endpoint: "/api/v1/users",
                type: USER_TYPE.Sales,
                state: handleSetSaleSearch,
                label: "Nhân viên kinh doanh",
                sx: { width: "250px", borderRadius: "6px" },
              },
              [USER_TYPE.Customer]: {
                endpoint: "/api/v1/customers",
                state: handleSetCustomerSearch,
                searchField: "fullName",
                label: "Khách hàng",
                sx: { width: "250px", borderRadius: "6px" },
              },
              [USER_TYPE.contextCode]: {
                searchField: "contextCode",
                label: "Mã đơn hàng",
                sx: { width: "250px", borderRadius: "6px" },
              },
              [USER_TYPE.Code]: {
                searchField: "code",
                label: "Mã phiếu xuất",
                sx: { width: "250px", borderRadius: "6px" },
              },
            }}
          />
        </Box>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleMultiplePayment}
          >
            Thanh toán hàng loạt
          </Button>
          <DebtSalesFilter
            variant="all"
            setFilter={setFilter}
            filter={filter}
          />
        </Stack>
      </Stack>
      <TableComp
        bodyList={bodyList}
        headers={header}
        sortData={sortData}
        setPagi={setPagi}
        pagi={pagi}
        total={total}
        extraTableRow={
          <TableRow sx={{ position: "sticky", bottom: 0, background: "#fff" }}>
            <TableCell colSpan={5}>
              <Typography
                fontWeight={700}
                textAlign="right"
              >
                Tổng:
              </Typography>
            </TableCell>
            <TableCell>
              <Typography
                fontWeight={700}
                align="center"
              >
                {formatCurrency(footerTotals.shippingFee ?? 0)}
              </Typography>
              <Typography sx={{ fontSize: "8px" }}>{convertCurrency(footerTotals.shippingFee ?? 0)} đồng</Typography>
            </TableCell>
            <TableCell>
              <Typography
                fontWeight="bold"
                sx={{
                  color: (footerTotals.debt ?? 0) < 0 ? DEBT_COLOR_INBOUND : DEBT_COLOR_OUTBOUND,
                }}
              >
                {(footerTotals.debt ?? 0) < 0 ? "-" : "+"}
                {formatCurrency(Math.abs(footerTotals.debt ?? 0))}
              </Typography>
              <Typography
                color="#989898"
                fontSize="8px"
                fontStyle="italic"
              >
                {convertCurrency(footerTotals.debt ?? 0)} đồng
              </Typography>
            </TableCell>
            <TableCell colSpan={4}></TableCell>
          </TableRow>
        }
        noBackground
      />
      <DebtSalesPaymentDialog
        open={!!selectedRow || isMultiplePayment}
        onClose={handleClosePayment}
        selectedRow={selectedRow}
        checkedList={checkedList}
        isMultiplePayment={isMultiplePayment}
        title={!selectedRow ? "Thanh toán hàng loạt công nợ" : `Thanh toán công nợ ${selectedRow?.code}`}
      />
      <DebtSalesHistoryDetailDialog
        open={openDetail}
        onClose={handleCloseDetail}
        selectedRow={detailRow}
        isAllTab
      />
    </>
  );
};

export default DebtSalesAllTab;
